/**
 * Deposit reversals — the money-IN mirror of withdrawal reversals.
 *
 * When the bank returns a wire that already backed an APPROVED deposit, an
 * agent proposes the reversal (naming the bank's reason) and a DIFFERENT
 * manager approves it with a fresh authenticator code. Approval posts the
 * compensating debit, moves the deposit to `reversed` and voids the wire.
 *
 * Agents see the "propose" form + their own proposals; managers see the
 * pending queue with approve / reject.
 */
import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import depositReversalsService, {
  type DepositReversal,
  type DepositReversalStatus,
} from '../../services/depositReversals.service';
import depositsService from '../../services/deposits.service';
import authService from '../../services/auth.service';
import { approveMfaErrorMessage } from '../../lib/mfaApproval';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime, formatMoney } from '../../lib/datetime';
import { fullName } from '../../lib/fullName';
import { colors, gradients } from '../../styles/theme';
import type { DepositRequest } from '../../types/finance';

const STATUS_TONE: Record<DepositReversalStatus, BadgeTone> = {
  pending: 'info',
  approved: 'success',
  rejected: 'danger',
};

export default function DepositReversals(): JSX.Element {
  const role = authService.getRole();
  const canPropose = role === 'agent' || role === 'super_admin';
  const canReview = role === 'manager' || role === 'super_admin';
  const [rows, setRows] = useState<DepositReversal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await depositReversalsService.list(showAll ? [] : ['pending']);
      setRows(res.items);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load deposit reversals.'));
    } finally {
      setLoading(false);
    }
  }, [showAll]);

  useEffect(() => { reload(); }, [reload]);

  const done = (msg: string): void => {
    setFlash(msg);
    globalThis.setTimeout(() => setFlash(null), 6000);
    reload();
  };

  return (
    <>
      <PageHeader
        title="Deposit reversals"
        subtitle="Unwind an approved deposit whose wire the bank returned. A different manager approves; the ledger gets a compensating debit."
        actions={
          <label className="text-xs inline-flex items-center gap-2" style={{ color: colors.inkSoft }}>
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
            <span>Show settled</span>
          </label>
        }
      />

      {flash && (
        <div className="rounded-lg p-3 mb-4 text-sm"
             style={{ background: colors.successBg, color: colors.success, border: `1px solid ${colors.successBorder}` }}>
          {flash}
        </div>
      )}
      {error && (
        <div className="rounded-lg p-3 mb-4 text-sm" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}

      {canPropose && <ProposeForm onProposed={done} />}

      <section className="mt-6">
        <h2 className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: colors.inkSoft }}>
          {showAll ? 'All reversals' : 'Pending approval'}
        </h2>
        {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}
        {!loading && rows.length === 0 && (
          <div className="rounded-2xl p-6 text-sm"
               style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}>
            No deposit reversals {showAll ? '' : 'awaiting approval'}.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <ul className="space-y-3">
            {rows.map((r) => (
              <li key={r.id} className="rounded-2xl p-4" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold" style={{ color: colors.ink }}>
                      {formatMoney(r.amount, r.currency_code)} · {r.product_name}
                    </div>
                    <div className="text-xs" style={{ color: colors.muted }}>
                      {r.client_email ?? ''} · deposit <span className="font-mono">{r.deposit_reference_number ?? '—'}</span> · reversal <span className="font-mono">{r.reference_number}</span>
                    </div>
                    {r.reason && <div className="text-xs mt-1" style={{ color: colors.inkSoft }}>{r.reason}</div>}
                    {r.rejection_reason && <div className="text-xs mt-1" style={{ color: colors.danger }}>Rejected: {r.rejection_reason}</div>}
                  </div>
                  <div className="text-right">
                    <StatusBadge tone={STATUS_TONE[r.status]}>{r.status}</StatusBadge>
                    <div className="text-xs mt-1" style={{ color: colors.mutedSoft }}>{formatDateTime(r.created_at)}</div>
                  </div>
                </div>
                {r.status === 'pending' && canReview && <ReviewPanel reversal={r} onDone={done} />}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function ProposeForm({ onProposed }: Readonly<{ onProposed: (msg: string) => void }>): JSX.Element {
  const [deposits, setDeposits] = useState<DepositRequest[]>([]);
  const [depositId, setDepositId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    depositsService.list({ status: ['approved'], size: 100 })
      .then((res) => setDeposits(res.items))
      .catch((e: unknown) => setError(getApiErrorMessage(e, 'Could not load approved deposits.')));
  }, []);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await depositReversalsService.propose(depositId, reason.trim());
      setDepositId('');
      setReason('');
      onProposed('Reversal proposed. A manager must approve it.');
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not propose the reversal.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl p-4" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <h2 className="text-sm font-bold uppercase tracking-widest mb-2" style={{ color: colors.inkSoft }}>Propose a reversal</h2>
      {error && (
        <div className="rounded-lg p-2 mb-2 text-xs" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <select value={depositId} onChange={(e) => setDepositId(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm" style={inputStyle}>
          <option value="">— approved deposit —</option>
          {deposits.map((d) => (
            <option key={d.id} value={d.id}>
              {d.reference_number ?? d.id.slice(0, 8)} · {fullName({ first_name: d.user_first_name, last_name: d.user_last_name })} · {formatMoney(d.amount, d.currency_code)}
            </option>
          ))}
        </select>
        <input value={reason} onChange={(e) => setReason(e.target.value)}
               placeholder="Bank return reason (min 5 characters)"
               className="px-3 py-2 rounded-lg text-sm" style={inputStyle} />
        <button type="button" onClick={() => { submit(); }} disabled={busy || !depositId || reason.trim().length < 5}
                className="px-3 py-2 rounded-lg text-sm font-semibold"
                style={{ background: gradients.gold, color: colors.navy, opacity: busy || !depositId || reason.trim().length < 5 ? 0.6 : 1 }}>
          {busy ? '…' : 'Propose reversal'}
        </button>
      </div>
    </section>
  );
}

function ReviewPanel({ reversal: r, onDone }: Readonly<{ reversal: DepositReversal; onDone: (msg: string) => void }>): JSX.Element {
  const [mode, setMode] = useState<'approve' | 'reject' | null>(null);
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (mode === 'approve') {
        await depositReversalsService.approve(r.id, r.version, code.trim());
        onDone('Reversal approved: compensating debit posted, wire voided.');
      } else {
        await depositReversalsService.reject(r.id, r.version, reason.trim());
        onDone('Reversal rejected; the deposit stays approved.');
      }
    } catch (e: unknown) {
      setError(mode === 'approve' ? approveMfaErrorMessage(e) || getApiErrorMessage(e, 'Approval failed.') : getApiErrorMessage(e, 'Rejection failed.'));
    } finally {
      setBusy(false);
    }
  };

  const ready = mode === 'approve' ? code.trim().length >= 6 : reason.trim().length >= 5;
  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
      {error && (
        <div className="rounded-lg p-2 mb-2 text-xs" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
      {!mode && (
        <div className="flex gap-2">
          <button type="button" onClick={() => setMode('approve')} className="px-3 py-1.5 rounded-md text-xs font-semibold"
                  style={{ background: gradients.gold, color: colors.navy }}>Approve</button>
          <button type="button" onClick={() => setMode('reject')} className="px-3 py-1.5 rounded-md text-xs font-semibold"
                  style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>Reject</button>
        </div>
      )}
      {mode && (
        <div className="flex flex-wrap items-center gap-2">
          {mode === 'approve' ? (
            <input type="text" inputMode="numeric" autoComplete="one-time-code" aria-label="Authentication code"
                   value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                   placeholder="6-digit code" className="px-3 py-2 rounded-lg text-sm font-mono tracking-widest w-36" style={inputStyle} />
          ) : (
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Rejection reason"
                   className="px-3 py-2 rounded-lg text-sm flex-1" style={inputStyle} />
          )}
          <button type="button" onClick={() => { run(); }} disabled={busy || !ready}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold"
                  style={{ background: gradients.gold, color: colors.navy, opacity: busy || !ready ? 0.6 : 1 }}>
            {busy ? '…' : 'Confirm'}
          </button>
          <button type="button" onClick={() => setMode(null)} className="px-3 py-1.5 rounded-md text-xs font-semibold"
                  style={{ background: 'transparent', color: colors.inkSoft, border: `1px solid ${colors.border}` }}>Cancel</button>
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  background: colors.canvas,
  color: colors.ink,
  border: `1px solid ${colors.border}`,
};
